/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("PageWidget", ["WidgetGroupBase"], function (context, cls) {
  /**
   * Page widget.
   * @class PageWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   * @publicdoc Widgets
   */
  cls.PageWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.PageWidget.prototype */ {
      __name: "PageWidget",

      /**
       * The title widget
       * @type {classes.PageTitleWidget}
       */
      _title: null,

      /** @function */
      _clickHandler: null,
      /** @function */
      _titleNavigateHandler: null,
      /** @function */
      _titleActivateHandler: null,
      /** @function */
      _onActivationHandler: null,
      /** @function */
      _pageActivateHandler: null,
      /** @function */
      _pageDisableHandler: null,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._title = cls.WidgetFactory.createWidget("PageTitle", this.getBuildParameters());
        this._clickHandler = this._title.when(
          context.constants.widgetEvents.click,
          function (event) {
            const folderWidget = this.getParentWidget();
            if (folderWidget) {
              folderWidget.onTitleClick(this);
              this.emit(context.constants.widgetEvents.click, event);
            }
          }.bind(this)
        );
        // Roving tablist keyboard navigation (accessibility mode): the title emits
        // tabNavigate; the folder moves focus to another tab (arrows → adjacent,
        // Home/End → first/last; this page is the reference). Mirrors the click
        // delegation just above.
        this._titleNavigateHandler = this._title.when(
          context.constants.widgetEvents.tabNavigate,
          function (event, src, direction) {
            this.getParentWidget()?.focusAdjacentTab(direction, this);
          }.bind(this)
        );
        // Space/Enter on the focused tab: activate this page but keep focus on
        // the tab (WAI-ARIA tablist, accessibility mode). Like a mouse click, a
        // folder PAGE with an associated ACTION must still fire it through to the
        // VM — so this mirrors the mouse delegation in _clickHandler above:
        // activateTab switches the page (keeping DOM focus on the tab), then the
        // page click drives OnClickUIBehavior (the page action).
        this._titleActivateHandler = this._title.when(
          context.constants.widgetEvents.tabActivate,
          function () {
            const folderWidget = this.getParentWidget();
            if (folderWidget) {
              folderWidget.activateTab(this);
              this.emit(context.constants.widgetEvents.click);
            }
          }.bind(this)
        );
        this._onActivationHandler = this.when(context.constants.widgetEvents.ready, this._onActivation.bind(this));
      },

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);
        // aria-controls on the tab must point to the element carrying role="tabpanel"
        // (the inner containerElement div), not to the widget root.
        // Symmetrically, aria-labelledby (which lives on the tabpanel) must reference the tab's id.
        this._containerElement.id = this.getRootClassName() + "__panel";
        this._title.setAriaAttribute("controls", this._containerElement.id);
        this.setAriaAttribute("labelledby", this._title.getRootClassName());
      },

      /**
       * @inheritDoc
       */
      getAriaElement: function () {
        return this._containerElement;
      },
      /**
       * @inheritDoc
       */
      _initLayout: function () {
        this._layoutInformation = new cls.LayoutInformation(this);
        this._layoutEngine = new cls.PageLayoutEngine(this);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._pageActivateHandler) {
          this._pageActivateHandler();
          this._pageActivateHandler = null;
        }
        if (this._pageDisableHandler) {
          this._pageDisableHandler();
          this._pageDisableHandler = null;
        }
        if (this._onActivationHandler) {
          this._onActivationHandler();
          this._onActivationHandler = null;
        }
        if (this._clickHandler) {
          this._clickHandler();
          this._clickHandler = null;
        }
        if (this._titleNavigateHandler) {
          this._titleNavigateHandler();
          this._titleNavigateHandler = null;
        }
        if (this._titleActivateHandler) {
          this._titleActivateHandler();
          this._titleActivateHandler = null;
        }
        this._title.destroy();
        this._title = null;

        $super.destroy.call(this);
      },

      activate: function () {
        this.emit(context.constants.widgetEvents.activate, this);
      },

      onActivate: function (hook) {
        return this.when(context.constants.widgetEvents.activate, hook);
      },

      disable: function () {
        this.emit(context.constants.widgetEvents.disable);
      },

      onDisable: function (hook) {
        return this.when(context.constants.widgetEvents.disable, hook);
      },

      _onActivation: function (event, widget, parentPageWidget) {
        if (parentPageWidget) {
          this._pageActivateHandler = parentPageWidget.onActivate(this.activate.bind(this));
          this._pageDisableHandler = parentPageWidget.onDisable(this.disable.bind(this));
        }
      },

      /**
       * Returns index of the pager in the parent folder
       * @returns {number} index of the page in the folder
       */
      getPageIndex: function () {
        const parent = this.getParentWidget();
        if (parent) {
          return parent.getChildren().indexOf(this);
        }
        return -1;
      },

      /**
       * @returns {classes.PageTitleWidget} the title widget
       */
      getTitleWidget: function () {
        return this._title;
      },

      /**
       * @inheritDoc
       */
      addChildWidget: function (widget, options) {
        if (this._children.length !== 0) {
          throw new Error("A page can only contain a single child");
        }
        $super.addChildWidget.call(this, widget, options);
      },

      /**
       * @param {string} text the text to display in the header
       */
      setText: function (text) {
        this._title.setText(text);
      },

      /**
       * @returns {string} the text to display in the header
       */
      getText: function () {
        return this._title.getText();
      },

      /**
       * @param {string} title the text to display in the title
       */
      setTitle: function (title) {
        this._title.setTitle(title);
      },

      /**
       * @return {string} the title of the page
       */
      getTitle: function () {
        return this._title.getTitle();
      },

      /**
       * @param {string} image the URL of the image or a font-image URL: font:[fontname]:[character]:[color] to display in the header
       */
      setImage: function (image) {
        this._title.setImage(image);
      },

      /**
       * @returns {string} the URL of the displayed image or a font-image URL: font:[fontname]:[character]:[color]
       */
      getImage: function () {
        return this._title.getImage();
      },

      /**
       * @inheritDoc
       */
      setHidden: function (hidden) {
        if (this._hidden === hidden) {
          return;
        }

        $super.setHidden.call(this, hidden);

        this._title.setHidden(hidden);
        // if un-hiding a non-current page
        if (!this.getParentWidget().getLateRendering() && this.isVisible() && !this.isCurrent()) {
          //Need to add this page back in dom to calculate its size in case it's bigger than the current page
          this.addInDom();
          this.getParentWidget().isMinified = false;
        }
        if (this.isVisible() !== this.isCurrent()) {
          // not visible and current: update to display another page
          // visible and not current: update in case no other page is visible
          this.getParentWidget().updateCurrentPage();
        }
        this.getParentWidget()?.pageVisibilityUpdated();
      },

      /**
       * @return {Boolean} True if this widget is the current page
       */
      isCurrent: function () {
        return this.getParentWidget()?.getCurrentPage() === this;
      },

      /**
       * @inheritDoc
       */
      isLayoutMeasureable: function (deep) {
        return true;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Page", cls.PageWidget);
});
